import { Button } from "antd"
import classNames from "classnames"
import React, { FC, useState } from "react"
import DrillInView, { DrillOption } from "./DrillInView"
import css from "./MobileFilterScreen.module.css"

// Beschreibt ein einzelnes Filterfeld in der Vollbild-Ansicht. Wird in
// CourseFilters aus derselben customerFilters-Guard-Logik wie der Desktop-Pfad
// abgeleitet (siehe getMobileFilterFields), damit beide dieselbe Sichtbarkeit teilen.
export type FilterFieldDescriptor = {
    key: string // CourseListFilters-Key, zugleich testid-Suffix (z.B. "courseTypeId")
    label: string
    valueText: string | null
    drill: {
        mode: "single" | "multi"
        title: string
        options: DrillOption[]
        selected: string | string[] | null
        searchable: boolean
        clearAllLabel?: string
        onPickSingle: (value: string | null) => void
        onApplyMulti: (values: string[]) => void
    }
}

type FilterFieldRowProps = {
    label: string
    valueText: string | null
    placeholder: string
    onOpen: () => void
    testId: string
}

// Tippbare Feldzeile: Label über dem Wert, Chevron rechts.
const FilterFieldRow: FC<FilterFieldRowProps> = ({ label, valueText, placeholder, onOpen, testId }) => (
    <div className={css.field}>
        <div className={css.fieldLabel}>{label}</div>
        <div
            className={classNames(css.fieldRow, valueText && css.fieldRowSet)}
            role='button'
            tabIndex={0}
            data-testid={testId}
            onClick={onOpen}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    onOpen()
                }
            }}
        >
            <span className={classNames(css.fieldValue, !valueText && css.fieldValuePlaceholder)}>{valueText ?? placeholder}</span>
            <span className={css.chev}>›</span>
        </div>
    </div>
)

export type MobileFilterScreenProps = {
    title: string
    fields: FilterFieldDescriptor[]
    onClose: () => void
    onReset: () => void
    onApply: () => void
    applyLabel: string
    applyDisabled: boolean
    applyLoading: boolean
    resetLabel: string
    closeLabel: string
    backLabel: string
    selectPlaceholder: string
    searchPlaceholder: string
    searchNoResultsLabel: string
    applySelectionLabel: string
}

// Vollbild-Filteransicht im Dokumentfluss (ersetzt den AntD-Bottom-Drawer).
// Drill-in wird INLINE gerendert (ersetzt die Feldliste), kein zweites Overlay.
const MobileFilterScreen: FC<MobileFilterScreenProps> = ({
    title,
    fields,
    onClose,
    onReset,
    onApply,
    applyLabel,
    applyDisabled,
    applyLoading,
    resetLabel,
    closeLabel,
    backLabel,
    selectPlaceholder,
    searchPlaceholder,
    searchNoResultsLabel,
    applySelectionLabel,
}) => {
    const [openField, setOpenField] = useState<string | null>(null)
    const activeField = openField ? fields.find((field) => field.key === openField) ?? null : null

    return (
        <div className={css.fullscreen} data-testid='mobile-filter-fullscreen'>
            {activeField ? (
                <DrillInView
                    // key pro Feld -> frische Such-/TEMP-Auswahl bei jedem Öffnen
                    key={activeField.key}
                    mode={activeField.drill.mode}
                    title={activeField.drill.title}
                    options={activeField.drill.options}
                    selected={activeField.drill.selected}
                    searchable={activeField.drill.searchable}
                    searchPlaceholder={searchPlaceholder}
                    searchNoResultsLabel={searchNoResultsLabel}
                    backLabel={backLabel}
                    clearAllLabel={activeField.drill.clearAllLabel}
                    applyLabel={applySelectionLabel}
                    onPickSingle={(value) => {
                        activeField.drill.onPickSingle(value)
                        setOpenField(null)
                    }}
                    onApplyMulti={(values) => {
                        activeField.drill.onApplyMulti(values)
                        setOpenField(null)
                    }}
                    onBack={() => setOpenField(null)}
                />
            ) : (
                <>
                    <header className={css.header}>
                        <span className={css.headerTitle}>{title}</span>
                        <button type='button' className={css.close} aria-label={closeLabel} onClick={onClose} data-testid='filter-close'>
                            ×
                        </button>
                    </header>
                    <div className={css.fieldList}>
                        {fields.map((field) => (
                            <FilterFieldRow
                                key={field.key}
                                label={field.label}
                                valueText={field.valueText}
                                placeholder={selectPlaceholder}
                                testId={`filter-field-row-${field.key}`}
                                onOpen={() => setOpenField(field.key)}
                            />
                        ))}
                    </div>
                    <footer className={css.actionBar}>
                        <Button className={css.resetButton} onClick={onReset}>
                            {resetLabel}
                        </Button>
                        <Button
                            type='primary'
                            className={css.applyButton}
                            disabled={applyDisabled}
                            loading={applyLoading}
                            onClick={onApply}
                            data-testid='mobile-filter-apply'
                        >
                            {applyLabel}
                        </Button>
                    </footer>
                </>
            )}
        </div>
    )
}

export default MobileFilterScreen
